import { useGetShareCode } from "@/service/pullNew.service";
import usePoster, { PosterResult } from "./usePoster";
import { useRef } from "react";
import bridge from "@/utils/dsBridge";
import {
	BridgeCallHandlers,
	BridgeClientShareReportKeys,
	BridgeClientShareTypes,
	ModulesMappings,
	UtmCampaign,
} from "@/interfaces/enums";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { getShareUrl } from "@/utils/shareWeb";
import getFullWebCdnUrl from "@/utils/getFullWebCdnUrl";
import { useProfile } from "@/service/common.services";

export default function useInviteShare(moduleKey: ModulesMappings) {
	const { t } = useTranslation("share");
	const { data: profile } = useProfile();
	const router = useRouter();
	const cacheRef = useRef<PosterResult>({ imageId: "", url: "" });

	const { shareCode = "" } = useGetShareCode();
	const { onShare } = usePoster();

	const handleBridgeShare = ({ imageId, url }: PosterResult) => {
		bridge.call(BridgeCallHandlers.ON_SHARE_BUTTON_CLICK, {
			shareTitle: getShareText() + "\n" + getCurrentPageLinkUrl(imageId),
			shareCode: shareCode,
			shareUrl: getCurrentPageLinkUrl(imageId),
			shareImgUrl: "",
			shareType: BridgeClientShareTypes.TEXT,
			shareScene: BridgeClientShareReportKeys.PULLING_NEW,
		});
	};

	const getShareText = () => {
		return t(`${moduleKey}-sub-title`, {
			inviteCode: shareCode,
			nickname: profile?.nickname,
		});
	};

	const getCurrentPageLinkUrl = (_imageId: string) => {
		if (typeof window === "undefined") {
			return "";
		}

		return getShareUrl({
			domain: router.locale === "en" ? window.location.origin : window.location.origin + "/" + router.locale,
			moduleKey,
			shareCode: shareCode,
			imageId: _imageId,
			utm_campaign: moduleKey === ModulesMappings.holiday2022 ? UtmCampaign.EARLY_BIRD : UtmCampaign.COMMISSION_REWARD,
		});
	};

	const onHandleShare = async () => {
		const _res = cacheRef.current;
		if (_res.imageId) {
			handleBridgeShare(_res);
			return;
		}

		try {
			const config = {
				w: 1200,
				h: 630,
				bg: getFullWebCdnUrl("/share/invite-poster.webp"),
				texts: [
					{
						txt: `${t("invite")}`,
						color: "#ffffff",
						fontStyle: "normal 32px sans-serif",
						letterSpacing: 2,
						maxWidth: 400,
						lineHeight: 40,
						x: 100,
						y: 450,
					},
					{
						txt: shareCode,
						color: "#ffffff",
						fontStyle: "bold 84px sans-serif",
						letterSpacing: 30,
						maxWidth: 600,
						lineHeight: 96,
						x: 100,
						y: 540,
					},
				],
			};

			const res = await onShare(config);
			if (!res) return;

			handleBridgeShare(res);
			cacheRef.current = { ...res };
		} catch (error) {
			console.log(error);
		}
	};

	return onHandleShare;
}
